<template>
  <v-dialog v-model="dialog" max-width="700px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ dialogTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <!-- Due Date Action - Date Picker -->
            <v-col cols="12" v-if="actionType === 'dueDate'">
              <v-menu
                ref="datePickerMenu"
                v-model="datePickerMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                :nudge-bottom="10"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedDueDate"
                    label="New Due Date *"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :locale="$i18n.locale"
                  v-model="tempDueDate"
                  :min="minDate"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="cancelDatePicker">
                    Cancel
                  </v-btn>
                  <v-btn text color="primary" @click="confirmDatePicker">
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>

            <!-- Assign Action - Assignee Selector -->
            <v-col cols="12" v-if="actionType === 'assign'">
              <v-autocomplete
                v-model="formData.assignee"
                :items="maintenanceTechnicians"
                :loading="isLoadingUsers"
                label="Assign To *"
                item-text="name"
                return-object
                clearable
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.isVendor">
                      Vendor - {{ item.email }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else>
                      Staff - {{ item.email }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-checkbox
                v-model="formData.sendEmail"
                label="Send email notification to assignee"
                :disabled="!formData.assignee"
              ></v-checkbox>
            </v-col>

            <!-- Priority Action - Priority Selector -->
            <v-col cols="12" v-if="actionType === 'priority'">
              <v-select
                v-model="formData.priority"
                :items="priorityOptions"
                label="New Priority *"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title :class="getPriorityColor(item)">
                      {{ item }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{ item }">
                  <span :class="getPriorityColor(item)">{{ item }}</span>
                </template>
              </v-select>
            </v-col>

            <!-- Cancel Action - Cancel Reason -->
            <v-col cols="12" v-if="actionType === 'cancel'">
              <p class="mb-4">
                You are about to cancel <strong>{{ selectedCount }}</strong> 
                {{ selectedCount === 1 ? 'maintenance request' : 'maintenance requests' }}. 
                This action cannot be undone.
              </p>
              
              <v-select
                v-model="formData.cancelReason"
                :items="cancelReasonOptions"
                label="What is the cancellation reason? *"
                required
              ></v-select>

              <v-textarea
                v-if="formData.cancelReason === 'Other'"
                v-model="formData.cancelReasonOther"
                label="Please specify the reason *"
                rows="3"
                required
              ></v-textarea>
            </v-col>
          </v-row>
        </v-container>
        <small>{{ $t('GeneralTranslations.StarIndicatesRequiredField') }}</small>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="close">
          {{ actionType === 'cancel' ? 'Discard' : $t('GeneralTranslations.Cancel') }}
        </v-btn>
        <v-btn 
          :color="submitButtonColor" 
          :disabled="!isFormValid" 
          :loading="loading"
          @click="submit"
        >
          {{ submitButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { MaintenancePriority } from '@/enums/MaintenancePriority';
import { sortItemsAsc } from '@/utils/sortItemsAsc';

export default {
  name: 'MassActionDialog',
  props: {
    actionType: {
      type: String,
      required: true,
      validator: (value) => ['cancel', 'dueDate', 'assign', 'priority'].includes(value)
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      isLoadingUsers: false,
      selectedRequestIds: [],
      datePickerMenu: false,
      tempDueDate: null,
      formData: {
        dueDate: null,
        assignee: null,
        priority: null,
        sendEmail: true,
        cancelReason: null,
        cancelReasonOther: null
      },
      cancelReasonOptions: [
        "Won't do",
        "Already completed",
        "No longer required",
        "Other"
      ]
    };
  },
  computed: {
    selectedCount() {
      return this.selectedRequestIds.length;
    },
    dialogTitle() {
      const titles = {
        cancel: 'Cancel Maintenance Requests',
        dueDate: 'Change Due Date',
        assign: 'Assign Maintenance Requests',
        priority: 'Change Priority'
      };
      return titles[this.actionType];
    },
    submitButtonText() {
      const texts = {
        cancel: 'Confirm Cancellation',
        dueDate: 'Update Due Date',
        assign: 'Assign Requests',
        priority: 'Update Priority'
      };
      return texts[this.actionType];
    },
    submitButtonColor() {
      return this.actionType === 'cancel' ? 'error' : 'primary';
    },
    isFormValid() {
      switch (this.actionType) {
        case 'cancel':
          if (!this.formData.cancelReason) return false;
          if (this.formData.cancelReason === 'Other' && !this.formData.cancelReasonOther) return false;
          return true;
        case 'dueDate':
          return !!this.formData.dueDate;
        case 'assign':
          return !!this.formData.assignee;
        case 'priority':
          return !!this.formData.priority;
        default:
          return false;
      }
    },
    minDate() {
      return moment().format('YYYY-MM-DD');
    },
    formattedDueDate() {
      if (!this.formData.dueDate) return '';
      const facility = this.$store.state.facilityModule.selectedFacility;
      return moment(this.formData.dueDate).format(facility?.dateFormat || 'DD/MM/YYYY');
    },
    maintenanceTechnicians() {
      const users = this.$store.state.userModule.maintenanceUsers || [];
      const vendors = this.$store.state.userModule.maintenanceVendors || [];
      const combined = [
        ...users,
        ...vendors.map(v => ({ ...v, isVendor: true }))
      ];
      return sortItemsAsc(combined, 'name');
    },
    priorityOptions() {
      return Object.values(MaintenancePriority);
    }
  },
  methods: {
    async open(requestIds) {
      this.selectedRequestIds = requestIds;
      this.resetForm();
      this.dialog = true;

      // Load maintenance users if needed for assign action
      if (this.actionType === 'assign' && !this.$store.state.userModule.maintenanceUsers?.length) {
        this.isLoadingUsers = true;
        await this.$store.dispatch('getMaintenanceUsers');
        await this.$store.dispatch('getMaintenanceVendors');
        this.isLoadingUsers = false;
      }
    },
    close() {
      this.dialog = false;
      this.resetForm();
      this.selectedRequestIds = [];
    },
    cancelDatePicker() {
      this.tempDueDate = this.formData.dueDate; // Reset to original value
      this.datePickerMenu = false;
    },
    confirmDatePicker() {
      this.formData.dueDate = this.tempDueDate; // Confirm the selected date
      this.datePickerMenu = false;
    },
    resetForm() {
      this.formData = {
        dueDate: null,
        assignee: null,
        priority: null,
        sendEmail: true,
        cancelReason: null,
        cancelReasonOther: null
      };
      this.tempDueDate = null;
    },
    async submit() {
      if (!this.isFormValid) return;

      this.loading = true;
      try {
        await this.performAction();
        this.$emit('completed');
        this.close();
      } catch (error) {
        console.error(`Error performing ${this.actionType} action:`, error);
        this.$toast.open({
          type: 'error',
          message: `Failed to ${this.actionType} maintenance requests`
        });
      } finally {
        this.loading = false;
      }
    },
    async performAction() {
      switch (this.actionType) {
        case 'cancel':
          await this.cancelRequests();
          break;
        case 'dueDate':
          await this.updateDueDate();
          break;
        case 'assign':
          await this.assignRequests();
          break;
        case 'priority':
          await this.updatePriority();
          break;
      }
    },
    async cancelRequests() {
      const cancelReason = this.formData.cancelReason === 'Other' 
        ? this.formData.cancelReasonOther 
        : this.formData.cancelReason;

      await this.$store.dispatch('batchCancelMaintenanceRequests', {
        requestIds: this.selectedRequestIds,
        cancelReason: cancelReason
      });

      this.$toast.open({
        type: 'success',
        message: `Successfully cancelled ${this.selectedCount} maintenance ${this.selectedCount === 1 ? 'request' : 'requests'}`
      });
    },
    async updateDueDate() {
      await this.$store.dispatch('batchUpdateMaintenanceRequests', {
        requestIds: this.selectedRequestIds,
        updates: {
          estimatedCompletionDate: this.formData.dueDate
        }
      });

      this.$toast.open({
        type: 'success',
        message: `Successfully updated due date for ${this.selectedCount} maintenance ${this.selectedCount === 1 ? 'request' : 'requests'}`
      });
    },
    async assignRequests() {
      await this.$store.dispatch('batchAssignMaintenanceRequests', {
        requestIds: this.selectedRequestIds,
        assignee: {
          id: this.formData.assignee.id,
          name: this.formData.assignee.name,
          email: this.formData.assignee.email,
          phoneNumber: this.formData.assignee.phoneNumber,
          isVendor: this.formData.assignee.isVendor || false
        },
        sendEmail: this.formData.sendEmail
      });

      this.$toast.open({
        type: 'success',
        message: `Successfully assigned ${this.selectedCount} maintenance ${this.selectedCount === 1 ? 'request' : 'requests'} to ${this.formData.assignee.name}`
      });
    },
    async updatePriority() {
      await this.$store.dispatch('batchUpdateMaintenanceRequests', {
        requestIds: this.selectedRequestIds,
        updates: {
          priority: this.formData.priority
        }
      });

      this.$toast.open({
        type: 'success',
        message: `Successfully updated priority to ${this.formData.priority} for ${this.selectedCount} maintenance ${this.selectedCount === 1 ? 'request' : 'requests'}`
      });
    },
    getPriorityColor(priority) {
      switch (priority) {
        case 'Urgent':
        case 'High':
          return 'red--text';
        case 'Medium':
          return 'orange--text';
        case 'Low':
          return 'green--text';
        default:
          return '';
      }
    }
  }
};
</script>

<style scoped>
/* Add any custom styling here if needed */
</style>
